/**
 * InformesView -- #/informes: los tres informes que el dueno pide en Excel (INF-T4, mision INFORMES-DUENO-V1-001).
 *
 * Boceto aprobado por Ares (#40159): tres pestanas (Egresos por concepto | Pagos a un proveedor | Financiero mensual),
 * filtros con los ultimos 12 meses por defecto, fail-open con el motivo y, en el celular, las tablas se leen por filas.
 * Audiencia: el dueno en el PC (1366x768) y en el celular.
 *
 * Las reglas y las hojas del Excel viven en application/informes/informesDelDueno.js; la descarga en exportarExcel.js,
 * que se importa SOLO al pulsar Exportar (exportacion con libreria estandar, carga diferida).
 *
 * @module presentation/views/InformesView
 */
import { AppConfig } from '../../config.js';
import { InformesApiClient } from '../../infrastructure/api/InformesApiClient.js';
import { getSelectedBusiness, getSelectedBusinessName } from '../components/BusinessSelector.js';
import { renderInformeVertical } from '../shared/NxInformeVertical.js';
import { esc } from '../shared/texto.js';
import {
  INFORMES, rangoPorDefecto, errorDeFechas, faltaProveedor, leerEgresos, leerFinanciero, estaVacio, pesos, nombreDelMes,
  columnasDelResumen, filaTotalDelResumen, hojasDelInforme, nombreDelArchivo, porQueNoHay,
} from '../../application/informes/informesDelDueno.js';
import { regresoHtml } from '../shared/regreso.js';

/** Filas del detalle que se pintan; el resto va completo en el Excel. */
const DETALLE_VISIBLE = 200;

const hoyBogota = () => new Date(Date.now() - 5 * 3600 * 1000).toISOString().slice(0, 10);

// Ambito propio: .form-input-sm declara flex-basis 160px y en una etiqueta que apila se vuelve ALTURA (trampa del panel).
const ESTILO = `
  .inf-view .form-input-sm { flex: none; width: auto; min-width: 0; }
  .inf-view .inf-filtros button { width: auto; }
  .inf-view .inf-tabs { display: flex; gap: 6px; flex-wrap: wrap; margin-top: 14px; }
  .inf-view .inf-tab[aria-selected="true"] { background: var(--accent-blue-strong); color: var(--text-on-accent); border-color: var(--accent-blue-strong); }
  .inf-view .inf-filtros { display: flex; flex-wrap: wrap; gap: 10px; align-items: flex-end; margin-top: 12px; padding: 12px;
    background: var(--bg-secondary); border: 1px solid var(--border-default); border-radius: 8px; }
  .inf-view .inf-filtros label { display: flex; flex-direction: column; gap: 4px; font-size: 0.875rem; color: var(--text-secondary); }
  .inf-view .inf-conceptos { max-height: 220px; overflow: auto; display: grid; gap: 4px; padding: 6px 0; }
  .inf-view .inf-conceptos label { flex-direction: row; align-items: center; gap: 6px; color: var(--text-heading); }
  .inf-view .inf-aviso { padding: 16px; background: var(--bg-secondary); border: 1px solid var(--border-default); border-radius: 8px; color: var(--accent-yellow); margin-top: 14px; }
  .inf-view .inf-bloque { margin-top: 18px; }
  .inf-view .inf-bloque h2 { font-size: 1rem; margin: 0 0 8px; }
  .inf-view .inf-tabla { width: 100%; border-collapse: collapse; font-size: 0.875rem; }
  .inf-view .inf-tabla th, .inf-view .inf-tabla td { padding: 6px 8px; border-bottom: 1px solid var(--border-default); text-align: left; }
  .inf-view .inf-tabla td.num, .inf-view .inf-tabla th.num { text-align: right; font-variant-numeric: tabular-nums; white-space: nowrap; }
  .inf-view .inf-tabla tr.inf-total td { font-weight: 600; border-top: 2px solid var(--border-default); }
  .inf-view .inf-resumen { color: var(--text-secondary); font-size: 0.875rem; margin-top: 12px; }
  @media (max-width: 600px) {
    .inf-view .inf-tabla thead { display: none; }
    .inf-view .inf-tabla tr { display: block; padding: 8px 0; border-bottom: 1px solid var(--border-default); }
    .inf-view .inf-tabla td { display: flex; justify-content: space-between; gap: 12px; border: 0; padding: 2px 0; text-align: right; }
    .inf-view .inf-tabla td::before { content: attr(data-l); color: var(--text-secondary); text-align: left; }
    .inf-view .inf-tabla td.num { white-space: normal; }
  }
`;

/** Una tabla que en el celular se lee por filas: cada celda lleva su etiqueta en data-l. cols: [{t, num}] */
function tabla(cols, filas, total) {
  const th = cols.map((c) => `<th${c.num ? ' class="num"' : ''}>${esc(c.t)}</th>`).join('');
  const tr = (f, extra = '') => `<tr${extra}>${f.map((v, i) => `<td data-l="${esc(cols[i].t)}"${cols[i].num ? ' class="num"' : ''}>${esc(v)}</td>`).join('')}</tr>`;
  return `<table class="inf-tabla"><thead><tr>${th}</tr></thead><tbody>${filas.map((f) => tr(f)).join('')}${total ? tr(total, ' class="inf-total"') : ''}</tbody></table>`;
}

function pintarEgresos(e, clave) {
  const cols = columnasDelResumen(e);
  const celda = (c, v) => (c.clave === 'concepto' ? v || '(sin concepto)' : c.clave === 'pct' ? v.toLocaleString('es-CO') + ' %' : pesos(v));
  const resumen = tabla(cols.map((c) => ({ t: c.titulo, num: c.clave !== 'concepto' })),
    e.resumen.map((r) => cols.map((c) => celda(c, r[c.clave]))),
    filaTotalDelResumen(e).map((v, i) => (i === 0 ? v : celda(cols[i], v))));
  const porMes = tabla([{ t: 'Mes' }, { t: 'Total', num: 1 }], e.porMes.map((m) => [nombreDelMes(m.mes), pesos(m.total)]), ['TOTAL', pesos(e.total)]);
  const visibles = e.detalle.slice(0, DETALLE_VISIBLE);
  const resto = e.detalle.length - visibles.length;
  const detalle = tabla([{ t: 'Fecha' }, { t: 'Concepto' }, { t: 'Método' }, { t: 'Total', num: 1 }],
    visibles.map((f) => [f.fecha, f.concepto, f.metodo, pesos(f.total)]));
  return `
    <p class="inf-resumen" data-inf-cuenta>${esc(clave === 'proveedor' ? 'Pagado' : 'Egresos')}: <strong>${esc(pesos(e.total))}</strong>
      en ${e.detalle.length.toLocaleString('es-CO')} registros, del ${esc(e.desde)} al ${esc(e.hasta)}.</p>
    <div class="inf-bloque"><h2>Resumen por concepto</h2>${resumen}</div>
    <div class="inf-bloque"><h2>Por mes</h2>${porMes}</div>
    <div class="inf-bloque"><h2>Detalle</h2>${detalle}
      ${resto > 0 ? `<p class="inf-resumen" data-inf-resto>Y ${resto.toLocaleString('es-CO')} registros más: van completos en el Excel.</p>` : ''}</div>`;
}

function pintarFinanciero(f) {
  const vertical = renderInformeVertical([{ ingresos: f.total.ingresos, egresos: f.total.egresos, utilidad: f.total.utilidad,
    margen_pct: f.total.pct, promedio_utilidad_mes: f.promedioUtilidad }]);
  const meses = tabla([{ t: 'Mes' }, { t: 'Ingresos', num: 1 }, { t: 'Egresos', num: 1 }, { t: 'Utilidad', num: 1 }, { t: '%', num: 1 }],
    f.meses.map((m) => [nombreDelMes(m.mes), pesos(m.ingresos), pesos(m.egresos), pesos(m.utilidad), m.pct + ' %']),
    ['TOTAL', pesos(f.total.ingresos), pesos(f.total.egresos), pesos(f.total.utilidad), f.total.pct + ' %']);
  return `
    <p class="inf-resumen">Del ${esc(f.desde)} al ${esc(f.hasta)}. Ingresos = ventas sin anuladas; egresos sin anulados, por fecha.</p>
    <div class="inf-bloque">${vertical}</div>
    <div class="inf-bloque"><h2>Por mes</h2>${meses}</div>`;
}

export async function renderInformes(container) {
  const rango = rangoPorDefecto(hoyBogota());
  const state = { clave: 'egresos', desde: rango.desde, hasta: rango.hasta, metodo: '', conceptos: [], todosLosConceptos: [],
    concepto: '', leido: null, cargando: false };

  container.innerHTML = `
    <style>${ESTILO}</style>
    <section class="artifact-view inf-view">
      <div class="artifact-header">
        <div>
          ${regresoHtml()}
          <h1>&#128209; Informes</h1>
          <p style="margin:4px 0 0;color:var(--text-secondary);font-size:0.875rem">Los informes del negocio para revisar o llevar a Excel.</p>
        </div>
      </div>
      <div class="inf-tabs" role="tablist">
        ${INFORMES.map((i) => `<button type="button" role="tab" class="btn-ghost btn-sm inf-tab" data-inf-tab="${i.clave}" aria-selected="${i.clave === state.clave}">${esc(i.nombre)}</button>`).join('')}
      </div>
      <form class="inf-filtros" data-inf-filtros></form>
      <div data-inf-salida></div>
    </section>`;

  const $form = container.querySelector('[data-inf-filtros]');
  const $salida = container.querySelector('[data-inf-salida]');

  container.querySelectorAll('[data-inf-tab]').forEach((b) => b.addEventListener('click', () => {
    if (state.cargando || b.dataset.infTab === state.clave) return;
    state.clave = b.dataset.infTab;
    state.leido = null;
    container.querySelectorAll('[data-inf-tab]').forEach((x) => x.setAttribute('aria-selected', String(x.dataset.infTab === state.clave)));
    pintarFiltros();
    cargar();
  }));

  function leerFiltros() {
    const v = (name) => $form.querySelector(`[name="${name}"]`)?.value ?? '';
    state.desde = v('desde'); state.hasta = v('hasta'); state.metodo = v('metodo');
    if (state.clave === 'egresos') state.conceptos = [...$form.querySelectorAll('[data-inf-concepto]:checked')].map((c) => c.value);
    if (state.clave === 'proveedor') state.concepto = v('concepto');
  }

  function pintarFiltros() {
    const metodo = state.clave === 'financiero' ? '' : `
      <label>Método
        <select name="metodo" class="form-input form-input-sm">
          ${[['', 'Todos'], ['Caja', 'Caja'], ['Externo', 'Externo']].map(([v, t]) => `<option value="${v}"${state.metodo === v ? ' selected' : ''}>${t}</option>`).join('')}
        </select>
      </label>`;
    const conceptos = state.clave !== 'egresos' ? '' : `
      <details data-inf-conceptos-caja>
        <summary style="cursor:pointer;color:var(--text-secondary);font-size:0.875rem">Conceptos (${state.conceptos.length ? state.conceptos.length + ' elegidos' : 'todos'})</summary>
        <div class="inf-conceptos">${state.todosLosConceptos.length
          ? state.todosLosConceptos.map((c) => `<label><input type="checkbox" data-inf-concepto value="${esc(c)}"${state.conceptos.includes(c) ? ' checked' : ''}/> ${esc(c || '(sin concepto)')}</label>`).join('')
          : '<span style="color:var(--text-muted)">Aparecen al ver el informe con todos los conceptos.</span>'}</div>
      </details>`;
    // Solo concepto, como la 1.0: los egresos no guardan el proveedor (Q #40165). Las sugerencias son los conceptos
    // reales del informe de egresos, para escribirlo exacto.
    const proveedor = state.clave !== 'proveedor' ? '' : `
      <label>Concepto del pago (tal como se registra en la caja)
        <input type="text" name="concepto" class="form-input form-input-sm" list="inf-conceptos-lista" value="${esc(state.concepto)}" placeholder="Ej.: Pago Proveedor X" style="min-width:16rem"/>
        <datalist id="inf-conceptos-lista">${state.todosLosConceptos.map((c) => `<option value="${esc(c)}"></option>`).join('')}</datalist>
      </label>`;
    $form.innerHTML = `
      <label>Desde<input type="date" name="desde" class="form-input form-input-sm" value="${esc(state.desde)}"/></label>
      <label>Hasta<input type="date" name="hasta" class="form-input form-input-sm" value="${esc(state.hasta)}"/></label>
      ${metodo}${proveedor}${conceptos}
      <button type="submit" class="btn-primary btn-sm" data-inf-ver>Ver informe</button>
      <button type="button" class="btn-ghost btn-sm" data-inf-exportar${state.leido ? '' : ' disabled'}>Exportar a Excel</button>`;
    $form.querySelector('[data-inf-exportar]').addEventListener('click', exportar);
  }

  $form.addEventListener('submit', (ev) => { ev.preventDefault(); leerFiltros(); cargar(); });

  function aviso(texto) {
    $salida.innerHTML = `<div class="inf-aviso" data-inf-aviso>${esc(texto)}</div>`;
  }

  function habilitarExportar() {
    const b = $form.querySelector('[data-inf-exportar]');
    if (b) b.disabled = !state.leido;
  }

  async function cargar() {
    state.leido = null;
    habilitarExportar();
    const negocio = getSelectedBusiness();
    if (!negocio) return aviso(porQueNoHay('SIN_NEGOCIO'));
    const malFecha = errorDeFechas(state.desde, state.hasta);
    if (malFecha) return aviso(malFecha);
    if (state.clave === 'proveedor') {
      const falta = faltaProveedor(state);
      if (falta) return aviso(falta);
    }
    state.cargando = true;
    $salida.innerHTML = '<p class="inf-resumen">Armando el informe…</p>';
    const clave = state.clave;
    const res = await InformesApiClient.leer(negocio, clave, state);
    state.cargando = false;
    if (clave !== state.clave) return;
    if (!res.ok) return aviso(porQueNoHay(res.motivo, AppConfig.env, res.detalle));
    const inf = clave === 'financiero' ? leerFinanciero(res.data) : leerEgresos(res.data);
    if (clave === 'egresos' && !state.conceptos.length) {
      state.todosLosConceptos = inf.resumen.map((r) => r.concepto).sort((a, b) => a.localeCompare(b, 'es'));
      pintarFiltros();
    }
    if (estaVacio(clave, inf)) return aviso(`No hay ${clave === 'financiero' ? 'ventas ni egresos' : 'egresos'} del ${state.desde} al ${state.hasta} con esos filtros.`);
    state.leido = inf;
    habilitarExportar();
    $salida.innerHTML = clave === 'financiero' ? pintarFinanciero(inf) : pintarEgresos(inf, clave);
  }

  async function exportar() {
    if (!state.leido) return;
    const b = $form.querySelector('[data-inf-exportar]');
    b.disabled = true; b.textContent = 'Preparando…';
    const { exportarExcel } = await import('../../application/informes/exportarExcel.js');
    const r = await exportarExcel(hojasDelInforme(state.clave, state.leido),
      nombreDelArchivo(state.clave, getSelectedBusinessName(), state.leido.desde || state.desde, state.leido.hasta || state.hasta));
    b.disabled = false; b.textContent = 'Exportar a Excel';
    if (!r.ok) aviso(r.detalle);
  }

  pintarFiltros();
  await cargar();
}
